import { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FundMovementsExperience from 'src/components/FundMovements/FundMovementsExperience';

import { MovementTypeChip } from './MovementTypeChip';
import { SummaryCards } from './SummaryCards';
import {
  formatCompetenceMonthLabel,
  mapChildrenToFundMovements,
} from './mapChildrenToFundMovements';

import type {
  FicChildTradesHistoryResponse,
  FundMovement,
  FundMovementsResponse,
  FundMovementsSummary,
  FundMovementsViewMode,
} from 'src/components/FundMovements/types';
import type { MetaCarteirasFicFidcsChildrenResponse } from 'src/services/meta-carteiras-fic-fidcs';

export interface FundMovementsModalProps {
  open: boolean;
  onClose: () => void;
  operationId: string;
  /** Data de competência no formato yyyy-MM-dd (mesma do componente pai). */
  competenceDate: string;
  /** Permite injetar dados (ex.: testes) sem chamar a API. */
  data?: FundMovementsResponse | null;
  /** Permite injetar histórico agregado nos testes */
  tradesHistoryData?: FicChildTradesHistoryResponse | null;
  /** Permite injetar dados brutos de children nos testes */
  childrenData?: MetaCarteirasFicFidcsChildrenResponse | null;
}

export {
  MovementTypeChip,
  SummaryCards,
  formatCompetenceMonthLabel,
  mapChildrenToFundMovements,
};

export type { FundMovement, FundMovementsResponse, FundMovementsSummary };

export default function FundMovementsModal({
  open,
  onClose,
  operationId,
  competenceDate,
  data: dataProp,
  tradesHistoryData: tradesHistoryDataProp,
  childrenData: childrenDataProp,
}: FundMovementsModalProps) {
  const [viewMode, setViewMode] = useState<FundMovementsViewMode>('chart');

  useEffect(() => {
    if (open) {
      setViewMode('chart');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1,
          fontWeight: 700,
        }}>
        <Typography component="span" variant="h6" fontWeight={700}>
          Movimentações
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) setViewMode(newMode);
            }}
            size="small"
            aria-label="Alternar visualização">
            <ToggleButton
              value="chart"
              aria-label="Gráfico"
              sx={{
                textTransform: 'none',
                px: 2,
                fontWeight: viewMode === 'chart' ? 700 : 500,
              }}>
              Gráfico
            </ToggleButton>
            <ToggleButton
              value="detailed"
              aria-label="Visão detalhada"
              sx={{
                textTransform: 'none',
                px: 2,
                fontWeight: viewMode === 'detailed' ? 700 : 500,
              }}>
              Visão detalhada
            </ToggleButton>
          </ToggleButtonGroup>

          <IconButton aria-label="fechar" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2, pb: 4 }}>
        <FundMovementsExperience
          operationId={operationId}
          initialCompetenceDate={competenceDate}
          showHeader={false}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          childrenData={childrenDataProp}
          tradesHistoryData={tradesHistoryDataProp}
          data={dataProp}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
